import { h, ComponentChildren } from 'preact';
import { useState, useCallback, useEffect, useRef } from 'preact/hooks';
import { forwardRef } from 'preact/compat';
import { css } from '@emotion/react';

interface DrawerProps {
  children: ComponentChildren;
  isOpen?: boolean;
  onClose?: () => void;
  className?: string;
  placement?: 'top' | 'end' | 'bottom' | 'start' | 'center';
  backdropColor?: string;
  preventCloseOnBackdropClick?: boolean;
}

interface DrawerRef {
  show: () => void;
  hide: () => void;
  isOpen: () => boolean;
}

// Inline styles
const styles = {
  drawerBackdrop: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    zIndex: 1000,
    overflow: 'hidden',
    touchAction: 'none'
  },
  drawer: {
    backgroundColor: 'rgb(26, 26, 26)',
    borderRadius: '16px 16px 0 0',
    width: '100%',
    maxWidth: '100%',
    overflowY: 'auto' as const,
    position: 'relative' as const,
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'block',
    margin: '0 auto'
  },
  centerDrawer: {
    backgroundColor: 'transparent',
    minWidth: '320px',
    maxWidth: '90vw',
    maxHeight: '90vh',
    width: 'auto',
    position: 'relative' as const,
    display: 'block',
    margin: '0 auto',
    overflowY: 'auto' as const
  }
};

const drawerContent = css`
  width: 100%;
  overflow: auto;
  margin: 0 auto;
`;

// Animation keyframes
const keyframes = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes fadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  
  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
  
  @keyframes slideDown {
    from { transform: translateY(0); }
    to { transform: translateY(100%); }
  }
  
  .backdrop-enter {
    animation: fadeIn 0.3s ease forwards;
  }
  
  .backdrop-leave {
    animation: fadeOut 0.3s ease forwards;
  }
  
  .drawer-enter {
    animation: slideUp 0.3s ease forwards;
  }
  
  .drawer-leave {
    animation: slideDown 0.3s ease forwards;
  }

  .fadeOut {
    animation: fadeOut 0.3s ease forwards;
  }

  .fadeIn {
    animation: fadeIn 0.3s ease forwards;
  }
`;

const Drawer = (
  {
    children,
    isOpen: propIsOpen = false,
    preventCloseOnBackdropClick = false,
    onClose,
    className = '',
    placement = 'bottom'
  }: DrawerProps,
  ref: React.ForwardedRef<DrawerRef>
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [animation, setAnimation] = useState<'enter' | 'leave' | null>(null);
  const [hasEntered, setHasEntered] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const drawerContentRef = useRef<HTMLDivElement>(null);
  const isClosingRef = useRef(false);

  useEffect(() => {
    if (isOpen && drawerRef.current) {
      const ownerDocument = drawerRef.current.ownerDocument;
      const styleElement = ownerDocument.createElement('style');
      styleElement.innerHTML = keyframes;
      ownerDocument.head.appendChild(styleElement);

      const originalBodyStyle = {
        overflow: ownerDocument.body.style.overflow,
        position: ownerDocument.body.style.position,
        width: ownerDocument.body.style.width
      };
      ownerDocument.body.style.overflow = 'hidden';
      ownerDocument.body.style.position = 'fixed';
      ownerDocument.body.style.width = '100%';

      return () => {
        if (ownerDocument.head.contains(styleElement)) {
          ownerDocument.head.removeChild(styleElement);
        }
        ownerDocument.body.style.overflow = originalBodyStyle.overflow;
        ownerDocument.body.style.position = originalBodyStyle.position;
        ownerDocument.body.style.width = originalBodyStyle.width;
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (propIsOpen) {
      if (!isOpen && !isClosingRef.current) {
        setIsOpen(true);
        requestAnimationFrame(() => {
          setTimeout(() => setAnimation('enter'), 0);
        });
      }
    } else {
      if (isOpen) {
        isClosingRef.current = true;
        setAnimation('leave');
        setHasEntered(false);
      }
    }
  }, [propIsOpen]);

  const handleAnimationEnd = useCallback(
    (e: AnimationEvent) => {
      if (e.target !== drawerContentRef.current) {
        return;
      }
      if (animation === 'leave') {
        setAnimation(null);
        setIsOpen(false);
        isClosingRef.current = false;
        if (onClose) {
          onClose();
        }
      } else if (animation === 'enter') {
        setAnimation(null);
        setHasEntered(true);
      }
    },
    [animation, onClose]
  );

  const hide = useCallback(() => {
    if (isOpen && !isClosingRef.current) {
      isClosingRef.current = true;
      setAnimation('leave');
      setHasEntered(false);
    }
  }, [isOpen]);

  const handleBackdropClick = useCallback(
    (e: MouseEvent) => {
      if (preventCloseOnBackdropClick) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      hide();
    },
    [hide]
  );

  const show = useCallback(() => {
    if (!isClosingRef.current && !isOpen) {
      setIsOpen(true);
      requestAnimationFrame(() => {
        setTimeout(() => setAnimation('enter'), 0);
      });
    }
  }, [isOpen]);

  const getIsOpen = useCallback(() => isOpen && !isClosingRef.current, [isOpen]);

  useEffect(() => {
    if (ref) {
      const methods = { show, hide, isOpen: getIsOpen };
      if (typeof ref === 'function') {
        ref(methods);
      } else if (ref) {
        ref.current = methods;
      }
    }
  }, [show, hide, getIsOpen, ref]);

  if (!isOpen) {
    return null;
  }

  const isCenter = placement === 'center';
  const containerStyle = {
    ...styles.drawerBackdrop,
    alignItems: isCenter ? 'center' : 'flex-end',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    pointerEvents: 'none' as const
  };

  const backdropStyle = {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    pointerEvents: 'auto' as const,
    opacity: animation === 'enter' || hasEntered ? 1 : 0,
    transition: 'opacity 0.3s'
  };

  const drawerStyle: any = isCenter
    ? { ...styles.centerDrawer, pointerEvents: 'auto' }
    : { ...styles.drawer, height: 'auto', pointerEvents: 'auto' };

  if (isOpen && !hasEntered && !animation && !isCenter) {
    drawerStyle.transform = 'translateY(100%)';
  }

  const animationClass = animation
    ? isCenter
      ? animation === 'leave'
        ? 'fadeOut'
        : animation === 'enter'
        ? 'fadeIn'
        : ''
      : `drawer-${animation}`
    : '';
  const backdropAnimationClass = animation ? `backdrop-${animation}` : '';

  return (
    <div ref={drawerRef} style={containerStyle}>
      <div style={backdropStyle} className={backdropAnimationClass} onClick={handleBackdropClick} />
      <div
        ref={drawerContentRef}
        style={{
          ...drawerStyle,
          opacity: isCenter && animation === null && !hasEntered ? 0 : 1
        }}
        className={`${isCenter && animation === 'leave' ? 'fadeOut' : animationClass} ${className}`}
        onAnimationEnd={handleAnimationEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={drawerContent.styles}>{children}</div>
      </div>
    </div>
  );
};

const DrawerWithRef = forwardRef<DrawerRef, DrawerProps>(Drawer);

export default DrawerWithRef;
